/**
 * A per-tab sessionStorage value with a lifetime: one key, one TTL, one shape.
 *
 * Three operations, none with hidden behaviour:
 *
 * - `read()` never writes storage, so repeating it changes nothing; in a
 *   component, read it through `@hooks/useSlotAtMountAndClear`. An expired,
 *   malformed or unreadable entry reads as `null` and stays where it is until
 *   `write`, `clear`, a sign-out sweep (@utils/storageCleanup) or the tab
 *   closing removes it.
 * - `write(value)` answers `true` only when storage really holds an entry that
 *   `read()` would return: `value` must pass `isValue` (so `write('')` on a
 *   non-empty-string slot is refused, not stored as something unreadable), and
 *   the raw string is read back (a partitioned WebView can accept a write and
 *   keep nothing). On any failure it also removes the previous entry (as far
 *   as storage allows), so a `false` does not leave an older value behind for
 *   the next read to mistake for this one.
 * - `clear()` removes the entry.
 *
 * None of them throws. There is deliberately no read-and-remove: spending a
 * value is a `clear()` at the place that decides it is spent, which keeps every
 * read safe to repeat.
 *
 * On disk: `{"value":<T>,"writtenAt":<epoch ms>}`, the same envelope the
 * magic-link mirrors use (@utils/magicLinkMirror reads through
 * `decodeEnvelope`). Expired is `Date.now() - writtenAt >= ttlMs`, one-sided,
 * with `ttlMs` chosen per slot (auth's 30-minute flows use `AUTH_FLOW_TTL_MS`,
 * @utils/authFlowTtl; a slot for another feature picks its own lifetime). See
 * `docs/session-slots.md` §2.5 for when a value belongs in a slot at all.
 */
export interface SessionSlot<T> {
  read(): T | null;
  write(value: T): boolean;
  clear(): void;
}

/** `null` is how a slot says "absent", so it is never a storable value. */
export type Storable = string | number | boolean | object;

export const encodeEnvelope = (value: Storable, writtenAt: number): string =>
  JSON.stringify({ value, writtenAt });

/**
 * The value inside `raw` if it is a well-formed envelope, younger than `ttlMs`
 * at `now`, holding something `isValue` accepts; otherwise `null`. Pure.
 */
export const decodeEnvelope = <T extends Storable>(
  raw: string | null,
  ttlMs: number,
  isValue: (value: unknown) => value is T,
  now: number,
): T | null => {
  if (raw === null) {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) {
    return null;
  }
  const { value, writtenAt } = parsed as { value?: unknown; writtenAt?: unknown };
  if (typeof writtenAt !== 'number' || now - writtenAt >= ttlMs) {
    return null;
  }
  return isValue(value) ? value : null;
};

/** The guard for a slot that only records THAT something happened, and when. */
export const isTrue = (value: unknown): value is true => value === true;

export const defineSessionSlot = <T extends Storable>({
  key,
  ttlMs,
  isValue,
}: {
  key: string;
  ttlMs: number;
  isValue: (value: unknown) => value is T;
}): SessionSlot<T> => {
  const clear = (): void => {
    try {
      globalThis.sessionStorage.removeItem(key);
    } catch {
      // Storage we cannot reach holds nothing we could read back either.
    }
  };

  return Object.freeze({
    read: (): T | null => {
      try {
        return decodeEnvelope(globalThis.sessionStorage.getItem(key), ttlMs, isValue, Date.now());
      } catch {
        return null;
      }
    },
    write: (value: T): boolean => {
      if (isValue(value)) {
        try {
          const raw = encodeEnvelope(value, Date.now());
          const storage = globalThis.sessionStorage;
          storage.setItem(key, raw);
          if (storage.getItem(key) === raw) {
            return true;
          }
        } catch {
          // Falls through to the clear: see `write` above.
        }
      }
      clear();
      return false;
    },
    clear,
  });
};
